import React, { useState } from 'react';
import { useAuth } from './hooks/useAuth';
import { AuthPage } from './pages/AuthPage';
import { MapView, ExclusionZone } from './components/MapView';
import { ParameterPanel } from './components/ParameterPanel';
import { ResultsPanel } from './components/ResultsPanel';
import { ProjectsSidebar } from './components/ProjectsSidebar';
import { LayoutRequest, LayoutResponse, SiteBBox } from './types';

const API = process.env.REACT_APP_API_URL || 'https://greendev-production.up.railway.app';

// ── Batch design types ────────────────────────────────────────────────────────

interface BatchVariant {
  gcr:         number;
  dc_ac_ratio: number;
  response:    LayoutResponse;
}

interface BatchDesignResponse {
  variants:       BatchVariant[];
  best_dc_idx:    number;
  best_yield_idx: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function App() {
  const { user, loading, login, register, logout } = useAuth();

  const [siteBox,     setSiteBox]     = useState<SiteBBox | null>(null);
  const [sitePolygon, setSitePolygon] = useState<number[][] | null>(null);
  const [response, setResponse]             = useState<LayoutResponse | null>(null);
  const [projectsKey, setProjectsKey]       = useState(0);
  const [exclusionZones, setExclusionZones] = useState<ExclusionZone[]>([]);

  // FIX: track currentRequest so batch design has a real base to work from
  const [currentRequest, setCurrentRequest] = useState<LayoutRequest | null>(null);

  // Batch design state
  const [showBatch, setShowBatch]       = useState(false);
  const [batchResults, setBatchResults] = useState<BatchDesignResponse | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError]     = useState<string | null>(null);
  const [batchGCR, setBatchGCR]         = useState('0.30,0.35,0.40,0.45,0.50');
  const [batchDCAC, setBatchDCAC]       = useState('1.20,1.30,1.40');

  // Export state
  const [exportLoading, setExportLoading] = useState<'dxf' | 'boq' | null>(null);

  // ── Handlers ──────────────────────────────────────────────────────────────

  // FIX: capture the request alongside the response so batch design can use it
  const handleResult = (res: LayoutResponse, req: LayoutRequest) => {
    setResponse(res);
    setCurrentRequest(req);
  };

  const handleProjectSaved  = () => setProjectsKey(k => k + 1);
  const handleProjectLoad   = (req: LayoutRequest, res: LayoutResponse) => {
    setResponse(res);
    setCurrentRequest(req);
  };
  const handleClearSite     = () => { setSiteBox(null); setSitePolygon(null); setResponse(null); setCurrentRequest(null); };

  const handleExclusionZoneAdded = (zone: ExclusionZone) =>
    setExclusionZones(prev => [...prev, zone]);
  const handleExclusionZoneClear = () => setExclusionZones([]);

  // ── Batch design ──────────────────────────────────────────────────────────

  const runBatchDesign = async () => {
    if (!currentRequest) {
      setBatchError('Run a layout first to use as the base for batch design.');
      return;
    }
    setBatchLoading(true);
    setBatchError(null);
    setBatchResults(null);
    try {
      const gcrValues  = batchGCR.split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v) && v > 0);
      const dcacValues = batchDCAC.split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v) && v > 0);
      if (!gcrValues.length || !dcacValues.length) throw new Error('Enter valid GCR and DC/AC values.');

      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API}/layout/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          base_request: currentRequest,
          gcr_values:   gcrValues,
          dcac_values:  dcacValues,
        }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail?.detail || `Server error ${res.status}`);
      }
      setBatchResults(await res.json());
    } catch (err: any) {
      setBatchError(err.message || 'Batch design failed');
    } finally {
      setBatchLoading(false);
    }
  };

  // ── DXF export ────────────────────────────────────────────────────────────

  const handleExportDXF = async () => {
    if (!response) return;
    setExportLoading('dxf');
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API}/layout/export/dxf?site_label=Solar+Farm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(response),
      });
      if (!res.ok) throw new Error('DXF export failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = 'solar_farm_layout.dxf'; a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(`DXF export failed: ${err.message}`);
    } finally {
      setExportLoading(null);
    }
  };

  // ── BOQ CSV export ────────────────────────────────────────────────────────

  const handleExportBOQ = async () => {
    if (!response) return;
    setExportLoading('boq');
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API}/layout/export/boq?site_label=Solar+Farm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(response),
      });
      if (!res.ok) throw new Error('BOQ export failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = 'solar_farm_boq.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(`BOQ export failed: ${err.message}`);
    } finally {
      setExportLoading(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ height: '100vh', background: 'linear-gradient(135deg,#1F3864,#17607A)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 36 }}>☀</div>
        <div style={{ color: 'rgba(255,255,255,0.7)', marginTop: 12, fontSize: 14 }}>Loading…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <AuthPage
        onLogin={async (e, p) => { await login(e, p); }}
        onRegister={async (e, p, n) => { await register(e, p, n); }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: "'Inter','Segoe UI',Arial,sans-serif" }}>

      {/* Left: Parameters */}
      <div style={{ width: 300, flexShrink: 0, boxShadow: '2px 0 8px rgba(0,0,0,0.12)', zIndex: 10, overflowY: 'auto' }}>
        <ParameterPanel
          siteBox={siteBox}
          sitePolygon={sitePolygon}
          onResult={handleResult}
          onProjectSaved={handleProjectSaved}
          userEmail={user.email}
          userPlan={user.plan}
          onLogout={logout}
        />
      </div>

      {/* Centre: Map */}
      <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
        <MapView
          response={response}
          siteBox={siteBox}
          exclusionZones={exclusionZones}
          onSiteDrawn={setSiteBox}
          onSitePolygonDrawn={setSitePolygon}
          onClearSite={handleClearSite}
          onExclusionZoneAdded={handleExclusionZoneAdded}
          onExclusionZoneClear={handleExclusionZoneClear}
        />
        <ProjectsSidebar refreshKey={projectsKey} onLoad={handleProjectLoad} />

        {/* Action bar — appears after first layout run */}
        {response && (
          <div style={actionBarStyle}>
            <button style={actionBtnStyle} disabled={exportLoading === 'dxf'} onClick={handleExportDXF}>
              {exportLoading === 'dxf' ? '⏳ Exporting…' : '📐 Export DXF'}
            </button>
            <button style={actionBtnStyle} disabled={exportLoading === 'boq'} onClick={handleExportBOQ}>
              {exportLoading === 'boq' ? '⏳ Exporting…' : '📋 Export BOQ'}
            </button>
            <button
              style={{ ...actionBtnStyle, background: showBatch ? 'rgba(46,95,163,0.9)' : actionBtnStyle.background as string }}
              onClick={() => { setShowBatch(p => !p); setBatchResults(null); setBatchError(null); }}
            >
              ⚡ Batch Design
            </button>
          </div>
        )}

        {/* Batch design panel */}
        {showBatch && response && (
          <BatchPanel
            gcr={batchGCR}
            dcac={batchDCAC}
            loading={batchLoading}
            error={batchError}
            results={batchResults}
            onGCRChange={setBatchGCR}
            onDCACChange={setBatchDCAC}
            onRun={runBatchDesign}   // FIX: now calls with currentRequest tracked in state
            onClose={() => setShowBatch(false)}
          />
        )}
      </div>

      {/* Right: Results */}
      {response && (
        <div style={{ width: 280, flexShrink: 0, boxShadow: '-2px 0 8px rgba(0,0,0,0.12)', zIndex: 10, overflowY: 'auto' }}>
          <ResultsPanel response={response} />
        </div>
      )}
    </div>
  );
}

// ── Batch panel ───────────────────────────────────────────────────────────────

interface BatchPanelProps {
  gcr: string; dcac: string; loading: boolean; error: string | null;
  results: BatchDesignResponse | null;
  onGCRChange: (v: string) => void; onDCACChange: (v: string) => void;
  onRun: () => void; onClose: () => void;
}

function BatchPanel({ gcr, dcac, loading, error, results, onGCRChange, onDCACChange, onRun, onClose }: BatchPanelProps) {
  return (
    <div style={batchPanelStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>⚡ Batch Design Sweep</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 16 }}>✕</button>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={batchLabelStyle}>GCR values (comma-separated)</label>
        <input value={gcr} onChange={e => onGCRChange(e.target.value)} style={batchInputStyle} placeholder="0.30,0.35,0.40,0.45,0.50" />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={batchLabelStyle}>DC/AC ratio values (comma-separated)</label>
        <input value={dcac} onChange={e => onDCACChange(e.target.value)} style={batchInputStyle} placeholder="1.20,1.30,1.40" />
      </div>

      <button onClick={onRun} disabled={loading} style={batchRunBtnStyle}>
        {loading ? '⏳ Running variants…' : '▶ Run Batch Design'}
      </button>

      {error && <div style={{ color: '#E74C3C', fontSize: 12, marginTop: 8 }}>{error}</div>}

      {results && (
        <div style={{ marginTop: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'left' }}>
                <th style={thStyle}>GCR</th>
                <th style={thStyle}>DC/AC</th>
                <th style={thStyle}>DC MWp</th>
                <th style={thStyle}>AC MW</th>
                <th style={thStyle}>MWh/yr</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {results.variants.map((v, i) => (
                <tr key={i} style={{
                  background: i === results.best_dc_idx
                    ? 'rgba(23,96,122,0.4)'
                    : i === results.best_yield_idx
                    ? 'rgba(46,95,163,0.3)'
                    : 'transparent',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <td style={tdStyle}>{v.gcr.toFixed(2)}</td>
                  <td style={tdStyle}>{v.dc_ac_ratio.toFixed(2)}</td>
                  <td style={tdStyle}>{v.response.geometry.total_dc_mwp.toFixed(1)}</td>
                  <td style={tdStyle}>{v.response.geometry.total_ac_mw.toFixed(1)}</td>
                  <td style={tdStyle}>{v.response.yield_.annual_mwh.toFixed(0)}</td>
                  <td style={tdStyle}>
                    {i === results.best_dc_idx    && <span title="Best DC MWp">⚡</span>}
                    {i === results.best_yield_idx && <span title="Best annual yield">☀</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, marginTop: 6 }}>
            ⚡ Best DC MWp &nbsp;&nbsp; ☀ Best annual yield
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const actionBarStyle: React.CSSProperties = {
  position: 'absolute', bottom: 16, right: 16, display: 'flex', gap: 8, zIndex: 20,
};
const actionBtnStyle: React.CSSProperties = {
  background: 'rgba(14,22,38,0.90)', color: '#fff',
  border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8,
  padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
  backdropFilter: 'blur(8px)', whiteSpace: 'nowrap' as const,
};
const batchPanelStyle: React.CSSProperties = {
  position: 'absolute', bottom: 64, right: 16, width: 520,
  background: 'rgba(14,22,38,0.95)', border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 12, padding: 16, backdropFilter: 'blur(10px)', zIndex: 25,
};
const batchLabelStyle: React.CSSProperties = {
  display: 'block', color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 4,
};
const batchInputStyle: React.CSSProperties = {
  width: '100%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 6, color: '#fff', padding: '6px 10px', fontSize: 12, boxSizing: 'border-box' as const,
};
const batchRunBtnStyle: React.CSSProperties = {
  width: '100%', background: 'rgba(23,96,122,0.8)', color: '#fff',
  border: '1px solid rgba(23,96,122,0.9)', borderRadius: 6,
  padding: '8px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4,
};
const thStyle: React.CSSProperties = { padding: '4px 8px', fontWeight: 500 };
const tdStyle: React.CSSProperties = { padding: '5px 8px', color: '#fff' };
